import React, { useState, useEffect, useMemo } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Tag, 
  Tooltip, 
  Select, 
  DatePicker,
  Badge,
  Progress
} from 'antd';
import { 
  FaKey, 
  FaSearch, 
  FaFilter,
  FaCalendarAlt,
  FaUser,
  FaBox,
  FaExclamationTriangle
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';
import dayjs from 'dayjs';
import './Licenca.css';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Licenca = () => {
  const [licencas, setLicencas] = useState([]);
  const [filteredLicencas, setFilteredLicencas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [partnerFilter, setPartnerFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState(null);
  const [uniquePartners, setUniquePartners] = useState([]);
  const [uniqueModules, setUniqueModules] = useState([]);
  const [expiringSoon, setExpiringSoon] = useState(0);
  const usuario = JSON.parse(localStorage.getItem('user'));

  const totalLicencas = useMemo(() => licencas.length, [licencas]);
  const licencasPorModulo = useMemo(() => {
    const counts = {};
    licencas.forEach(lic => {
      const modulo = lic.modulo || 'NÃO DEFINIDO';
      counts[modulo] = (counts[modulo] || 0) + 1;
    });
    return counts;
  }, [licencas]);

  const sms_error = (sms) => toast.error(sms, { position: "top-right", autoClose: 3000 });
  const sms_success = (sms) => toast.success(sms, { position: "top-right", autoClose: 2000 });

  const fetchLicencas = async () => {
    setLoading(true);
    try {
      const dados = { id: usuario.id };
      const response = await Api.ListLicenca(dados);
      
      if (response.success && Array.isArray(response.licencas)) {
        const formattedLicencas = response.licencas.map((item, index) => ({
          key: item.id,
          id: item.id,
          cliente: item.Nome,
          licenca: item.Keyword,
          dias: item.DiasRestantes,
          tipo: item.Tipo,
          keyIn: item.KeyIn,
          keyOut: item.KeyOut,
          modulo: item.modulo,
          nome_parceiro: item.nome_parceiro || 'Sem parceiro',
          numero: index + 1,
          status: getStatus(item.DiasRestantes)
        }));
        
        setLicencas(formattedLicencas);
        setFilteredLicencas(formattedLicencas);
        
        const expiring = formattedLicencas.filter(l => l.dias < 30 && l.dias > 0).length;
        setExpiringSoon(expiring);
        
        const partners = [...new Set(response.licencas
          .filter(item => item.nome_parceiro)
          .map(item => item.nome_parceiro))];
        setUniquePartners(partners);
        
        const modules = [...new Set(response.licencas
          .filter(item => item.modulo)
          .map(item => item.modulo))];
        setUniqueModules(modules);
      } else {
        sms_error('Formato de resposta inválido');
      }
    } catch (error) {
      sms_error('Erro ao buscar licenças: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatus = (dias) => {
    if (dias <= 0) return 'expirada';
    if (dias <= 7) return 'critica';
    if (dias <= 30) return 'alerta';
    return 'ativa';
  };

  const getStatusColor = (dias) => {
    if (dias <= 0) return '#ff4d4f';
    if (dias <= 7) return '#ffa940';
    if (dias <= 30) return '#faad14';
    return '#52c41a';
  };

  const getStatusTag = (dias) => {
    const status = getStatus(dias);
    const colors = { ativa: 'green', alerta: 'orange', critica: 'volcano', expirada: 'red' };
    const texts = { ativa: 'Ativa', alerta: 'Alerta', critica: 'Crítica', expirada: 'Expirada' };
    return <Tag color={colors[status]}>{texts[status]}</Tag>;
  };

  useEffect(() => { fetchLicencas(); }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    if (!value.trim()) {
      applyFilters({}, partnerFilter, moduleFilter, statusFilter, dateFilter);
      return;
    }
    const filtered = licencas.filter(item =>
      item.cliente?.toLowerCase().includes(value.toLowerCase()) ||
      item.licenca?.toLowerCase().includes(value.toLowerCase()) ||
      item.nome_parceiro?.toLowerCase().includes(value.toLowerCase()) ||
      item.modulo?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredLicencas(filtered);
  };

  const applyFilters = (filters, partner, module, status, dateRange) => {
    let filtered = [...licencas];
    Object.entries(filters).forEach(([key, val]) => {
      if (val) filtered = filtered.filter(item => item[key]?.toString().toLowerCase().includes(val.toLowerCase()));
    });
    if (partner) filtered = filtered.filter(item => item.nome_parceiro?.toLowerCase().includes(partner.toLowerCase()));
    if (module) filtered = filtered.filter(item => item.modulo?.toLowerCase() === module.toLowerCase());
    if (status) filtered = filtered.filter(item => item.status === status.toLowerCase());
    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      filtered = filtered.filter(item => {
        const keyInDate = dayjs(item.keyIn);
        return keyInDate.isAfter(start.startOf('day')) && keyInDate.isBefore(end.endOf('day'));
      });
    }
    setFilteredLicencas(filtered);
  };

  const handleFilter = (field, value) => {
    const newFilters = { ...activeFilters, [field]: value };
    setActiveFilters(newFilters);
    applyFilters(newFilters, partnerFilter, moduleFilter, statusFilter, dateFilter);
  };

  const handlePartnerFilter = (value) => { setPartnerFilter(value); applyFilters(activeFilters, value, moduleFilter, statusFilter, dateFilter); };
  const handleModuleFilter = (value) => { setModuleFilter(value); applyFilters(activeFilters, partnerFilter, value, statusFilter, dateFilter); };
  const handleStatusFilter = (value) => { setStatusFilter(value); applyFilters(activeFilters, partnerFilter, moduleFilter, value, dateFilter); };
  const handleDateFilter = (dates) => { setDateFilter(dates); applyFilters(activeFilters, partnerFilter, moduleFilter, statusFilter, dates); };

  const clearFilters = () => {
    setActiveFilters({});
    setPartnerFilter('');
    setModuleFilter('');
    setStatusFilter('');
    setDateFilter(null);
    setSearchText('');
    setFilteredLicencas(licencas);
  };

  const columns = [
    {
      title: 'Cliente',
      dataIndex: 'cliente',
      key: 'cliente',
      width: 180,
      render: (text) => (
        <div className="client-cell">
          <span className="client-name">{text}</span>
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="filter-dropdown">
          <Input
            placeholder="Buscar por cliente"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => { handleFilter('cliente', selectedKeys[0]); confirm(); }}
          />
          <div className="filter-actions">
            <Button type="primary" onClick={() => { handleFilter('cliente', selectedKeys[0]); confirm(); }}>Filtrar</Button>
            <Button onClick={() => { clearFilters(); handleFilter('cliente', ''); }}>Limpar</Button>
          </div>
        </div>
      ),
      filterIcon: () => <FaFilter />,
    },
    {
      title: 'Licença',
      dataIndex: 'licenca',
      key: 'licenca',
      width: 160,
      render: (text) => (
        <div className="license-cell">
          <Tooltip title={text}>
            <span className="license-text">{text}</span>
          </Tooltip>
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="filter-dropdown">
          <Input
            placeholder="Buscar por licença"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => { handleFilter('licenca', selectedKeys[0]); confirm(); }}
          />
          <div className="filter-actions">
            <Button type="primary" onClick={() => { handleFilter('licenca', selectedKeys[0]); confirm(); }}>Filtrar</Button>
            <Button onClick={() => { clearFilters(); handleFilter('licenca', ''); }}>Limpar</Button>
          </div>
        </div>
      ),
      filterIcon: () => <FaFilter />,
    },
    {
      title: 'Dias',
      dataIndex: 'dias',
      key: 'dias',
      width: 130,
      align: 'center',
      sorter: (a, b) => a.dias - b.dias,
      render: (dias) => (
        <div className="days-cell">
          <div className="days-container">
            <Badge count={dias} style={{ backgroundColor: getStatusColor(dias), fontWeight: 'bold' }} />
            <div style={{ marginTop: 4 }}>{getStatusTag(dias)}</div>
            {dias > 0 && dias <= 365 && (
              <Progress
                percent={Math.round((dias / 365) * 100)}
                size="small"
                status={dias <= 30 ? "exception" : "normal"}
                strokeColor={getStatusColor(dias)}
                showInfo={false}
              />
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Data',
      dataIndex: 'keyIn',
      key: 'keyIn',
      width: 110,
      align: 'center',
      render: (date) => (
        <div className="date-cell">
          <span>{dayjs(date).format('DD/MM/YYYY')}</span>
        </div>
      ),
      sorter: (a, b) => dayjs(a.keyIn).unix() - dayjs(b.keyIn).unix(),
    },
    {
      title: 'Módulo',
      dataIndex: 'modulo',
      key: 'modulo',
      width: 130,
      align: 'center',
      render: (modulo) => (
        <div className="module-cell">
          <Tag color="cyan" className="module-tag">{modulo}</Tag>
        </div>
      ),
      filters: uniqueModules.map(mod => ({ text: mod, value: mod })),
      onFilter: (value, record) => record.modulo === value,
    },
  ];

  const hasActiveFilters = Object.keys(activeFilters).length > 0 || partnerFilter || moduleFilter || statusFilter || dateFilter;

  return (
    <div className="licenca-container">
      <ToastContainer />

      {/* Estatísticas */}
      <Row gutter={[16, 16]} className="stats-row">
        <Col xs={24} sm={12}>
          <Card className="stat-card total-card">
            <Statistic
              title="Total de Licenças"
              value={totalLicencas}
              prefix={<FaKey />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div className="stat-trend"><span>Licenças registradas</span></div>
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card className="stat-card warning-card">
            <Statistic
              title="Prestes a Expirar"
              value={expiringSoon}
              prefix={<FaExclamationTriangle />}
              valueStyle={{ color: '#faad14' }}
            />
            <div className="stat-trend"><span>Expira em menos de 30 dias</span></div>
          </Card>
        </Col>
      </Row>

      {/* Distribuição por Módulo */}
      <Card className="distribution-card">
        <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FaBox /> Distribuição por Módulo
        </h3>
        <Row gutter={[16, 16]}>
          {Object.entries(licencasPorModulo).map(([modulo, quantidade]) => (
            <Col xs={24} sm={12} md={6} key={modulo}>
              <div className="module-distribution">
                <div className="module-name">{modulo}</div>
                <div className="module-count">{quantidade}</div>
                <Progress
                  percent={Math.round((quantidade / totalLicencas) * 100)}
                  size="small"
                  strokeColor="#52c41a"
                />
              </div>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Filtros */}
      <Card className="toolbar-card">
        <Row gutter={[12, 12]} align="bottom">
          {/* Pesquisa */}
          <Col xs={24} md={12} lg={8}>
            <div className="filter-group">
              <label>Pesquisa</label>
              <Search
                placeholder="Cliente, licença, parceiro ou módulo..."
                prefix={<FaSearch />}
                enterButton="Buscar"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                onSearch={handleSearch}
                allowClear
              />
            </div>
          </Col>

          {/* Data */}
          <Col xs={24} md={12} lg={5}>
            <div className="filter-group">
              <label><FaCalendarAlt style={{ marginRight: 4 }} />Data de Solicitação</label>
              <RangePicker
                style={{ width: '100%' }}
                placeholder={['Início', 'Fim']}
                value={dateFilter}
                onChange={handleDateFilter}
                format="DD/MM/YYYY"
              />
            </div>
          </Col>

          {/* Parceiro */}
          <Col xs={24} sm={8} lg={3}>
            <div className="filter-group">
              <label><FaUser style={{ marginRight: 4 }} />Parceiro</label>
              <Select
                style={{ width: '100%' }}
                placeholder="Todos"
                value={partnerFilter || undefined}
                onChange={handlePartnerFilter}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {uniquePartners.map((partner, i) => (
                  <Option key={i} value={partner}>{partner}</Option>
                ))}
              </Select>
            </div>
          </Col>

          {/* Módulo */}
          <Col xs={24} sm={8} lg={3}>
            <div className="filter-group">
              <label><FaBox style={{ marginRight: 4 }} />Módulo</label>
              <Select
                style={{ width: '100%' }}
                placeholder="Todos"
                value={moduleFilter || undefined}
                onChange={handleModuleFilter}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {uniqueModules.map((modulo, i) => (
                  <Option key={i} value={modulo}>{modulo}</Option>
                ))}
              </Select>
            </div>
          </Col>

          {/* Status */}
          <Col xs={24} sm={8} lg={3}>
            <div className="filter-group">
              <label><FaExclamationTriangle style={{ marginRight: 4 }} />Status</label>
              <Select
                style={{ width: '100%' }}
                placeholder="Todos"
                value={statusFilter || undefined}
                onChange={handleStatusFilter}
                allowClear
              >
                <Option value="ativa">Ativa</Option>
                <Option value="alerta">Alerta</Option>
                <Option value="critica">Crítica</Option>
                <Option value="expirada">Expirada</Option>
              </Select>
            </div>
          </Col>

          {/* Botão limpar */}
          <Col xs={24} sm={24} lg={2}>
            <Button
              type="default"
              onClick={clearFilters}
              style={{ width: '100%' }}
              disabled={!hasActiveFilters && !searchText}
            >
              Limpar
            </Button>
          </Col>
        </Row>

        {/* Tags de filtros ativos */}
        {hasActiveFilters && (
          <div className="active-filters">
            <span className="filters-label">Filtros ativos:</span>
            {Object.entries(activeFilters).map(([key, value]) =>
              value ? (
                <Tag key={key} closable onClose={() => handleFilter(key, '')} color="blue">
                  {key === 'cliente' ? 'Cliente' : key === 'licenca' ? 'Licença' : key}: {value}
                </Tag>
              ) : null
            )}
            {partnerFilter && <Tag closable onClose={() => handlePartnerFilter('')} color="green">Parceiro: {partnerFilter}</Tag>}
            {moduleFilter && <Tag closable onClose={() => handleModuleFilter('')} color="cyan">Módulo: {moduleFilter}</Tag>}
            {statusFilter && <Tag closable onClose={() => handleStatusFilter('')} color="purple">Status: {statusFilter}</Tag>}
            {dateFilter && (
              <Tag closable onClose={() => handleDateFilter(null)} color="orange">
                Data: {dayjs(dateFilter[0]).format('DD/MM/YYYY')} - {dayjs(dateFilter[1]).format('DD/MM/YYYY')}
              </Tag>
            )}
          </div>
        )}
      </Card>

      {/* Tabela com scroll horizontal no mobile */}
      <div className="table-responsive-wrapper">
        <Card className="table-card">
          <Table
            columns={columns}
            dataSource={filteredLicencas}
            loading={loading}
            scroll={{ x: 'max-content' }} /* ✅ scroll horizontal automático */
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
              pageSizeOptions: ['10', '20', '50', '100'],
              responsive: true,
            }}
            rowClassName={(record, index) => {
              let cls = index % 2 === 0 ? 'table-row-even' : 'table-row-odd';
              if (record.dias <= 0) cls += ' expired-row';
              else if (record.dias <= 7) cls += ' critical-row';
              else if (record.dias <= 30) cls += ' warning-row';
              return cls;
            }}
            locale={{
              emptyText: hasActiveFilters || searchText
                ? 'Nenhuma licença com os filtros atuais'
                : 'Nenhuma licença cadastrada',
            }}
          />
        </Card>
      </div>
    </div>
  );
};

export default Licenca;